import { Platform, HapticType } from "../platform.ts";
import { cssVariablesDark, cssVariablesLight } from "../../../ui/theme.tsx";
import { PlatformSchemaType } from "api";
import { makeObservable, observable, action, computed } from "mobx";
import { LanguageShared } from "api";
import { getWebApp } from "./telegram-web-app.ts";
import { cloudStorageAdapter } from "./cloud-storage.ts";
import { lockOrientationWhenPortrait } from "./lock-orientation-when-portrait.ts";
import { applyColorScheme } from "../../color-scheme/apply-color-scheme.ts";
import { normalizeLanguage } from "../../../translations/normalize-language.ts";

const LANGUAGE_CACHE_KEY = "languageCached";
const appBackgroundColorVariable = "--tg-theme-secondary-bg-color";

export class TelegramPlatform implements Platform {
  isFullScreen = this.calcIsFullScreen();
  languageCached: LanguageShared | null = null;
  isMainButtonRegistered = false;
  mainButtonCondition?: () => boolean;

  constructor() {
    makeObservable(this, {
      isFullScreen: observable,
      languageCached: observable,
      isMainButtonRegistered: observable,
      isMainButtonVisible: computed,
      registerMainButton: action,
      unregisterMainButton: action,
    });

    if (!this.isCloudStorageAvailable()) {
      return;
    }

    // We use cloud storage to cache language so we know it before we load user preferences
    cloudStorageAdapter.getItem(LANGUAGE_CACHE_KEY).then(
      action((languageCached) => {
        if (!languageCached) {
          return;
        }
        this.languageCached = normalizeLanguage(languageCached);
      }),
    );
  }

  get isMainButtonVisible() {
    return (
      this.isMainButtonRegistered &&
      (this.mainButtonCondition === undefined || this.mainButtonCondition())
    );
  }

  registerMainButton(condition?: () => boolean) {
    this.mainButtonCondition = condition;
    this.isMainButtonRegistered = true;
  }

  unregisterMainButton() {
    this.isMainButtonRegistered = false;
    this.mainButtonCondition = undefined;
  }

  getInitData(): string {
    return getWebApp().initData;
  }

  getUserAvatarUrl(): string | null {
    return getWebApp().initDataUnsafe.user?.photo_url ?? null;
  }

  private getCssVariables() {
    return getWebApp().colorScheme === "dark"
      ? cssVariablesDark
      : cssVariablesLight;
  }

  private applyTheme() {
    const webApp = getWebApp();

    applyColorScheme(webApp.colorScheme);

    const cssVariables = this.getCssVariables();
    for (const [variable, value] of Object.entries(cssVariables)) {
      document.documentElement.style.setProperty(variable, value);
    }

    if (webApp.colorScheme === "dark") {
      webApp.setHeaderColor(cssVariables[appBackgroundColorVariable]);
    } else {
      webApp.setHeaderColor("secondary_bg_color");
    }

    webApp.setBackgroundColor(cssVariables[appBackgroundColorVariable]);
  }

  getStartParam(): string | undefined {
    const startParam = getWebApp().initDataUnsafe.start_param;
    if (startParam) return startParam;

    const urlParams = new URLSearchParams(window.location.search);
    const start = urlParams.get("start");
    return typeof start === "string" ? start : undefined;
  }

  initialize() {
    this.applyTheme();

    getWebApp().ready();
    if (this.isSwipeControllable()) {
      getWebApp().isVerticalSwipesEnabled = false;
    }
    getWebApp().expand();
    this.requestFullscreen();
    lockOrientationWhenPortrait();

    getWebApp().onEvent(
      "fullscreenChanged",
      action(() => {
        this.isFullScreen = this.calcIsFullScreen();
      }),
    );
    getWebApp().onEvent("themeChanged", () => {
      this.applyTheme();
    });
  }

  private calcIsFullScreen() {
    if (!getWebApp().isVersionAtLeast("8.0")) {
      return false;
    }
    // Telegram Desktop for macOS reports fullscreen state unreliably.
    if (getWebApp().platform === "macos") {
      return true;
    }
    return !!getWebApp().isFullscreen;
  }

  private requestFullscreen() {
    if (!getWebApp().isVersionAtLeast("8.0")) {
      return;
    }

    if (!this.isMobile) {
      return;
    }

    getWebApp().requestFullscreen();
  }

  isOutdated(): boolean {
    return !getWebApp().isVersionAtLeast("6.1");
  }

  isCloudStorageAvailable(): boolean {
    return getWebApp().isVersionAtLeast("6.9");
  }

  isSwipeControllable() {
    return getWebApp().isVersionAtLeast("7.8");
  }

  openInternalLink(link: string) {
    // https://github.com/overtake/TelegramSwift/issues/1156
    if (this.isMacosWithShareBugs()) {
      this.openExternalLink(link);
      return;
    }

    getWebApp().openTelegramLink(link);
  }

  isShareMessageAvailable() {
    return getWebApp().isVersionAtLeast("8.0");
  }

  shareMessage(preparedMessageId: string) {
    getWebApp().shareMessage(preparedMessageId);
  }

  isIos() {
    return getWebApp().platform === "ios";
  }

  isWeb() {
    return (
      getWebApp().platform === "webk" ||
      getWebApp().platform === "weba" ||
      getWebApp().platform === "web"
    );
  }

  isAndroid() {
    return getWebApp().platform === "android";
  }

  get isMobile() {
    return this.isIos() || this.isAndroid();
  }

  isMacosWithShareBugs() {
    return (
      getWebApp().platform === "macos" && getWebApp().isVersionAtLeast("7.8")
    );
  }

  getClientData(): PlatformSchemaType {
    return {
      platform: getWebApp().platform,
      userAgent: navigator.userAgent,
      colorScheme: getWebApp().colorScheme,
      tgVersion: getWebApp().version,
    };
  }

  setLanguageCached(language: LanguageShared) {
    this.languageCached = language;
    cloudStorageAdapter.setItem(LANGUAGE_CACHE_KEY, language);
  }

  getLanguageCached(): LanguageShared {
    if (this.languageCached) {
      return this.languageCached;
    }
    // If there's no cached language - use Telegram's language before we load user preferences

    return normalizeLanguage(getWebApp().initDataUnsafe.user?.language_code);
  }

  openExternalLink(link: string) {
    getWebApp().openLink(link);
  }

  openInvoiceLink(link: string) {
    getWebApp().openInvoice(link, (status) => {
      if (status === "paid") {
        getWebApp().close();
      }
    });
  }

  hideKeyboard() {
    const webApp = getWebApp();
    if ("hideKeyboard" in webApp) {
      webApp.hideKeyboard();
    }
  }

  haptic(type: HapticType) {
    if (!this.isMobile) return;

    switch (type) {
      case "error":
      case "success":
      case "warning":
        getWebApp().HapticFeedback.notificationOccurred(type);
        break;
      case "light":
      case "medium":
      case "heavy":
        getWebApp().HapticFeedback.impactOccurred(type);
        break;
      case "selection":
        getWebApp().HapticFeedback.selectionChanged();
        break;
      default:
        return type satisfies never;
    }
  }
}
